import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ContainerClusterEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useContainerClusterAction} from "./useContainerClusterAction";

const ALL_CONTAINER_CLUSTER_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type ContainerClusterAction = typeof ALL_CONTAINER_CLUSTER_ACTIONS[number];

export type ContainerClusterActionMenuDropdownProps = {
  containerCluster: ContainerClusterEntity
  actions?: ContainerClusterAction[]
  onActionClick?: (action: ContainerClusterAction) => void
  showEmpty?: boolean
}

export const ContainerClusterActionMenuDropdown = (props: ContainerClusterActionMenuDropdownProps) => {
  const {
    containerCluster,
    actions = ALL_CONTAINER_CLUSTER_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ContainerClusterAction> = useMemo(() => {
    const enabledActions = new Set<ContainerClusterAction>()
    const actionsSet = actions ? new Set<ContainerClusterAction>(actions) : new Set<ContainerClusterAction>()

    if (hasAccess("containercluster_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("containercluster_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [containerCluster, actions, hasAccess])

  const {
    onContainerClusterActionClick
  } = useContainerClusterAction()

  const _onActionClick = useCallback((action: ContainerClusterAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onContainerClusterActionClick(containerCluster, action)
    }
  }, [onActionClick, onContainerClusterActionClick, containerCluster])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
